import { convertToCapitalizedString } from 'central-utils/stringUtils';
import PaymentStatusMessage, { PaymentStatusType } from 'components/common/PaymentStatusMessage';
import { transferLocalToSessionAndClear } from 'central-utils/storageUtils';
import usePreventBackNavigation from 'hooks/usePreventBackNavigation';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetPaymentStatusByOrderIdQuery } from 'store/checkout/checkoutAPISlice';

const MAX_POLL_RETRIES = 10;
const POLL_INTERVAL_MS = 5000;

/**
 * Terminal statuses where polling should stop.
 * SUCCESS = payment completed, FAILED = payment failed.
 */
const TERMINAL_STATUSES = ['SUCCESS', 'FAILED'];

/**
 * In-progress statuses where polling should continue.
 * CREATED = Razorpay order created, payment in progress.
 */
const IN_PROGRESS_STATUSES = ['CREATED', 'PENDING', 'AUTHORIZING', 'PENDING_VBV'];

const PaymentStatusPage: React.FC = () => {
  usePreventBackNavigation('/payment-status');

  const navigate = useNavigate();

  const [isTransferComplete, setIsTransferComplete] = useState<boolean>(false);
  const [pollCount, setPollCount] = useState<number>(0);
  const [polling, setPolling] = useState<boolean>(true);
  const [countdown, setCountdown] = useState<number>(5);

  // Restore session storage from local storage (Razorpay redirects may clear session)
  useEffect(() => {
    const performTransfer = async () => {
      try {
        await transferLocalToSessionAndClear();
        setIsTransferComplete(true);
      } catch (error) {
        console.error('Error during session transfer:', error);
        setIsTransferComplete(true);
      }
    };
    performTransfer();
  }, []);

  // Detect which flow we are in
  const razorpayOrderId = isTransferComplete ? sessionStorage.getItem('RazorpayOrderId') : null;
  const isRazorpayFlow = !!razorpayOrderId;

  const orderId = isTransferComplete ? razorpayOrderId || sessionStorage.getItem('OrderId') : null;

  const tenantDetails = JSON.parse(sessionStorage.getItem('CustomerApp-Tenant') || '{}');

  // ---------- Razorpay flow: poll payment status API ----------
  const {
    data: paymentStatusData,
    isLoading,
    isError,
    isSuccess,
    refetch,
  } = useGetPaymentStatusByOrderIdQuery(
    { orderId: orderId },
    { skip: !orderId || !isRazorpayFlow },
  );

  const status = paymentStatusData?.status ?? null;
  const apiShouldPoll = paymentStatusData?.shouldPoll;

  /**
   * Polling logic:
   * - Continue polling while status is in-progress (CREATED, PENDING, etc.)
   * - Also respect the API's `shouldPoll` field when present
   * - Stop on terminal statuses (SUCCESS, FAILED) or after max retries
   */
  useEffect(() => {
    if (!isRazorpayFlow || !orderId || !polling) return;

    // Stop polling on terminal statuses
    if (status && TERMINAL_STATUSES.includes(status)) {
      setPolling(false);
      return;
    }

    // If the API explicitly says stop polling
    if (apiShouldPoll === false && status !== null) {
      setPolling(false);
      return;
    }

    // Max retries reached
    if (pollCount >= MAX_POLL_RETRIES) {
      setPolling(false);
      return;
    }

    const intervalId = setInterval(() => {
      setPollCount((prev) => {
        if (prev + 1 >= MAX_POLL_RETRIES) {
          setPolling(false);
        }
        return prev + 1;
      });
      refetch();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [isRazorpayFlow, orderId, polling, status, apiShouldPoll, pollCount, refetch]);

  // Redirect countdown on FAILED status
  useEffect(() => {
    if (!isRazorpayFlow || status !== 'FAILED') return;

    const timerId = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          navigate('/check-out');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerId);
  }, [isRazorpayFlow, status, navigate]);

  // ---------- Old flow: static data from sessionStorage ----------
  const [oldFlowData, setOldFlowData] = useState<any>(null);

  useEffect(() => {
    if (!isRazorpayFlow && isTransferComplete) {
      const stored = sessionStorage.getItem('PaymentStatusAPIData');
      if (stored && stored.length > 0) {
        try {
          setOldFlowData(JSON.parse(stored));
        } catch {
          // ignore parse errors
        }
      }
    }
  }, [isRazorpayFlow, isTransferComplete]);

  // ---------- Render ----------
  const tenantName = tenantDetails?.name ? convertToCapitalizedString(tenantDetails.name) : null;

  const thankYouMessage = tenantName
    ? `Thanks for choosing ${tenantName}, come back soon for more fun!`
    : `Thanks for choosing us, come back soon for more fun!`;

  // --- Old flow (non-Razorpay): static success page ---
  if (!isRazorpayFlow) {
    return <PaymentStatusMessage status='SUCCESS' thankYouMessage={thankYouMessage} />;
  }

  // --- Razorpay flow: dynamic status ---
  const renderRazorpayStatus = () => {
    let currentStatus: PaymentStatusType = 'PROCESSING';
    if (status === 'SUCCESS') currentStatus = 'SUCCESS';
    else if (status === 'FAILED') currentStatus = 'FAILED';
    else if (!polling && pollCount >= MAX_POLL_RETRIES) currentStatus = 'UNKNOWN';

    return (
      <PaymentStatusMessage
        status={currentStatus}
        thankYouMessage={thankYouMessage}
        responseMessage={paymentStatusData?.responseMessage}
        orderId={paymentStatusData?.orderId ?? orderId}
        countdown={countdown}
        onRetry={() => navigate('/check-out')}
      />
    );
  };

  return (
    <div className='h-[calc(100vh-64px)] paymentStatusPage flex items-center justify-center px-6'>
      {renderRazorpayStatus()}
    </div>
  );
};

export default PaymentStatusPage;
